import { BufferGeometry, Intersection, Mesh, Object3D, Raycaster, Sphere } from 'three';
import { acceleratedRaycast, MeshBVH, MeshBVHOptions } from 'three-mesh-bvh';

/**
 * The fewest triangles of a mesh that it is raycast through a bounds tree for. Fewer are tested as
 * fast one by one, without the memory of a tree.
 */
const minBoundsTreeTriangles = 500;

const sphere = new Sphere();

const countTriangles = (geometry: BufferGeometry) =>
  (geometry.index?.count ?? geometry.getAttribute('position')?.count ?? 0) / 3;

/**
 * Raycasts a mesh through a bounds tree of its geometry, made the first time the ray reaches it,
 * if it has many triangles. Raycaster.firstHitOnly gets only its closest hit.
 */
function raycastThroughBoundsTree(this: Mesh, raycaster: Raycaster, intersects: Intersection[]) {
  const { geometry } = this;

  if (!geometry.boundsTree && countTriangles(geometry) < minBoundsTreeTriangles) {
    Mesh.prototype.raycast.call(this, raycaster, intersects);
    return;
  }

  // As Mesh.raycast does first, which the bounds tree doesn't.
  if (!geometry.boundingSphere) {
    geometry.computeBoundingSphere();
  }
  sphere.copy(geometry.boundingSphere!).applyMatrix4(this.matrixWorld);
  if (!raycaster.ray.intersectsSphere(sphere)) {
    return;
  }

  // Indirect, which leaves the order of the triangles of the geometry as it is, as it can be shared
  // with other meshes, e.g. by the objects of an IFC type. Not in the typings of this version.
  geometry.boundsTree ??= new MeshBVH(geometry, { indirect: true } as MeshBVHOptions);
  acceleratedRaycast.call(this, raycaster, intersects);
}

/**
 * Raycasts the meshes below the object through bounds trees, e.g. the many objects of an IFC model
 * that are picked as the mouse moves over them. Meshes that are raycast in a way of their own, such
 * as texts, are left as they are.
 */
export function raycastThroughBoundsTrees(object: Object3D) {
  object.traverse((x) => {
    if (x instanceof Mesh && x.raycast === Mesh.prototype.raycast) {
      x.raycast = raycastThroughBoundsTree;
    }
  });
}
